import React from 'react';
import { createPortal } from 'react-dom';
import { SlidersHorizontal } from 'lucide-react';

/**
 * FilterFab — Mobile-only floating "Filtres" pill (Booking/Airbnb iOS pattern).
 *
 * - Rendered via `createPortal(document.body)` to escape `backdrop-blur` /
 *   `transform` containing blocks that would otherwise trap `position: fixed`.
 * - Sits above the BottomNavMobile (z-30, ~64-72px tall).
 * - Hidden on `md:` and up (desktop usually shows the filter row inline).
 *
 * @param {boolean}  open       Current open/expanded state.
 * @param {function} onToggle   Called on click; flip parent state.
 * @param {number}   activeCount  Active filter count shown as orange badge.
 * @param {string}   openLabel  Label when expanded (default "Fermer").
 * @param {string}   closedLabel Label when collapsed (default "Filtres").
 * @param {string}   ariaControls id of the element the button controls.
 * @param {string}   testId      data-testid override.
 */
const FilterFab = ({
  open,
  onToggle,
  activeCount = 0,
  openLabel = 'Fermer',
  closedLabel = 'Filtres',
  ariaControls,
  testId = 'filters-mobile-fab',
}) => {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={ariaControls}
      data-testid={testId}
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 84px)' }}
      className="md:hidden fixed left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 rounded-full bg-stone-900 px-5 py-3 text-sm font-semibold text-white shadow-2xl shadow-black/30 ring-1 ring-white/10 transition-colors hover:bg-stone-800 active:scale-95"
    >
      <SlidersHorizontal className="h-4 w-4" />
      <span>{open ? openLabel : closedLabel}</span>
      {activeCount > 0 && (
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-500 px-1.5 text-label font-bold text-white">
          {activeCount}
        </span>
      )}
    </button>,
    document.body,
  );
};

export default FilterFab;
